import React, { useEffect } from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import { Toaster } from 'sonner';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { LanguageProvider } from './contexts/LanguageContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { initAdMob, showBanner, hideBanner, prepareInterstitial, prepareRewarded } from './lib/admob';
import { initPush } from './lib/push';
import { api, saveSessionToken } from './lib/api';
import Layout from './components/Layout';
import RequireAuth from './components/RequireAuth';
import Splash from './components/Splash';
import UpdatePrompt from './components/UpdatePrompt';
import Home from './pages/Home';
import Category from './pages/Category';
import RecipeDetail from './pages/RecipeDetail';
import Search from './pages/Search';
import Favorites from './pages/Favorites';
import ShoppingList from './pages/ShoppingList';
import Premium from './pages/Premium';
import WeekPlan from './pages/WeekPlan';
import Tracker from './pages/Tracker';
import Login from './pages/Login';
import Privacy from './pages/Privacy';
import Trending from './pages/Trending';
import AuthCallback from './pages/AuthCallback';
import ScrollRestoration from './components/ScrollRestoration';

function AppRouter() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  // Initialize AdMob banners on native shells (no-op on web).
  // Hide banner for premium subscribers — ad-free is a key Premium benefit.
  useEffect(() => {
    initAdMob().then(() => {
      if (user?.is_premium) hideBanner();
      else { showBanner(); prepareInterstitial(); prepareRewarded(); }
    });
    initPush();
  }, [user?.is_premium]);

  // Keep the device screen on while the user is inside the app — essential
  // for hands-free cooking. `KeepAwake.keepAwake()` is a no-op on web and
  // toggles the Android `FLAG_KEEP_SCREEN_ON` window flag on native. We
  // also release the lock when the app goes to background, so screen-off
  // behaviour returns to normal once the user leaves.
  useEffect(() => {
    const enable = async () => {
      try { await KeepAwake.keepAwake(); } catch { /* web / not supported */ }
    };
    const release = async () => {
      try { await KeepAwake.allowSleep(); } catch { /* ignore */ }
    };
    enable();
    const stateSub = CapApp.addListener('appStateChange', ({ isActive }) => {
      if (isActive) enable(); else release();
    });
    return () => {
      release();
      stateSub.then?.(s => s.remove?.());
    };
  }, []);

  // Listen for deep links coming back from Chrome Custom Tabs (Google OAuth).
  // When the browser hands off `https://cook-smart-40.emergent.host/#session_id=…`
  // via the AndroidManifest intent filter, `appUrlOpen` fires. We finish the
  // sign-in *inline* here (exchange session_id → session_token, hydrate the
  // auth context, navigate home). Doing this directly — instead of bouncing
  // through AuthCallback via hash mutation — is necessary because changing
  // `window.location.hash` does NOT re-trigger React Router v6 routing, which
  // is why the previous attempt left users stranded on /login.
  useEffect(() => {
    const isNative = typeof window !== 'undefined'
      && window.Capacitor?.isNativePlatform?.();
    if (!isNative) return;
    const handle = CapApp.addListener('appUrlOpen', async (event) => {
      try {
        const incoming = event?.url || '';
        const match = incoming.match(/session_id=([^&#]+)/);
        if (!match) return;
        const sessionId = match[1];
        // Close the Custom Tab so the user lands on the app, not behind it.
        Browser.close().catch(() => {});
        // Exchange the one-time session_id for a long-lived session token.
        const { data } = await api.post('/auth/session', { session_id: sessionId });
        if (data?.session_token) saveSessionToken(data.session_token);
        if (data?.user) setUser(data.user);
        navigate('/', { replace: true });
      } catch (e) {
        console.warn('[appUrlOpen] sign-in failed', e);
        navigate('/login', { replace: true });
      }
    });
    return () => { handle.then((h) => h.remove()).catch(() => {}); };
  }, [navigate, setUser]);

  // CRITICAL: Detect session_id in URL fragment synchronously, before any other routing
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <>
      <ScrollRestoration />
      <Routes>
        <Route element={<Layout />}>
          {/* Public routes — login + privacy stay accessible without auth */}
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          {/* Everything else requires login */}
          <Route element={<RequireAuth />}>
            <Route path="/" element={<Home />} />
            <Route path="/category/:id" element={<Category />} />
            <Route path="/recipe/:id" element={<RecipeDetail />} />
            <Route path="/search" element={<Search />} />
            <Route path="/trending" element={<Trending />} />
            <Route path="/favorites" element={<Favorites />} />
            <Route path="/shopping" element={<ShoppingList />} />
            <Route path="/premium" element={<Premium />} />
            <Route path="/week-plan" element={<WeekPlan />} />
            <Route path="/tracker" element={<Tracker />} />
          </Route>
        </Route>
      </Routes>
    </>
  );
}

function App() {
  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <BrowserRouter>
            <AuthProvider>
              <Splash />
              <UpdatePrompt />
              <AppRouter />
              <Toaster position="top-center" richColors />
            </AuthProvider>
          </BrowserRouter>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
